import { getViewport } from "./ViewPort";

export const LANDSCAPE = "L";
export const PORTRAIT = "P";

/**
 * BreakPoint data will have breakpoint values as properties.
 *     {
 *         Infinity: true,
 *         1024: false,
 *         767: 'maybe',
 *     }
 * Note that the Infinity value is used to represent the largest
 * imaginable (or unimaginable) width possible. This value MUST
 * always be present. Any number may be used to represent any width
 * desired and all other widths are optional.
 */
export interface BreakpointData {
  [key: number]: any;
}

/**
 * Returns the current breakpoint of the page.
 * Leverages the Viewport util. This works server side,
 * but will return a default value as there is no breakpoint server side.
 */
export const getCurrentBreakpoint = (): number => getViewport().right;

/**
 * Returns "L" when landscape, and "P" when portrait.
 */
export const getCurrentOrientation = (): string => (getViewport().bottom > getViewport().right ? PORTRAIT : LANDSCAPE);

/**
 * Returns the highest value from BreakpointData closest to the width passed.
 * For example, if the BreakPointData object
 *     {
 *         Infinity: true,
 *         1024: false,
 *         767: 'maybe',
 *     }
 * is passed with the width of 1162, false will be returned.
 * If no data is passed, it will return the optional passed default.
 * If the passed data is an empty object or an object with only a
 * "variationID" property, the optional passed default will be returned.
 * If the passed data is a string or an Array, the data will be returned.
 * If the highest value found (lower than the passed width) is undefined,
 * the optional passed default will be returned.
 *
 * @param data
 * @param width
 * @param defaults
 */
export const getDerivedValueAtWidth = (
  data: BreakpointData,
  width: number,
  defaults?: any
): any => {
  if (
    !data ||
    (!Array.isArray(data) &&
      typeof data === "object" &&
      // eslint-disable-next-line no-prototype-builtins
      ((Object.keys(data).length < 2 && data.hasOwnProperty("variationID")) ||
        Object.keys(data).length === 0))
  ) {
    return defaults;
  }
  if (
    typeof data === "string" ||
    typeof data === "boolean" ||
    typeof data === "number" ||
    typeof data === "function" ||
    Array.isArray(data) ||
    (!Array.isArray(data) &&
      typeof data === "object" &&
      // eslint-disable-next-line no-prototype-builtins
      !data.hasOwnProperty("Infinity") &&
      // eslint-disable-next-line no-prototype-builtins
      !data.hasOwnProperty(Infinity))
  ) {
    return data;
  }
  let found = Infinity;
  for (const key in data) {
    if (data[key] || data[key] === `` || data[key] === false) {
      const currentKey: number = parseFloat(key);
      if (currentKey < found && currentKey >= width) {
        found = currentKey;
      }
    }
  }
  return typeof data[found] !== "undefined" ? data[found] : defaults;
};

export const getDerivedValueAtWidthAndOrientation = (
  data: BreakpointData,
  breakpoint: number,
  orientation?: string,
  defaults?: any
): any => {
  const breakpointData = getDerivedValueAtWidth(data, breakpoint, defaults);
  const o = orientation ? orientation.toLowerCase() : LANDSCAPE.toLowerCase();
  return breakpointData && breakpointData[o] ?
    breakpointData[o] :
    breakpointData;
};
